import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {MutationObserver | null} */
  #progressObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#initShippingProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#progressObserver?.disconnect();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.open();
      this.#initShippingProgress();
    }
  };

  #initShippingProgress() {
    this.#progressObserver?.disconnect();
    this.#progressObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'data-progress' && mutation.target instanceof HTMLElement) {
          this.#animateProgress(mutation.target);
        }

        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (!(node instanceof HTMLElement)) continue;
            const els = node.matches('.progress[data-progress]')
              ? [node]
              : [...node.querySelectorAll('.progress[data-progress]')];
            for (const el of els) {
              if (el instanceof HTMLElement) {
                requestAnimationFrame(() => {
                  el.getBoundingClientRect(); // force layout flush before transitioning
                  this.#animateProgress(el);
                });
              }
            }
          }
        }
      }
    });

    this.#progressObserver.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-progress'],
    });
  }

  /** @param {HTMLElement} el */
  #animateProgress(el) {
    const progress = Math.min(parseFloat(el.dataset.progress ?? '0') || 0, 100);
    el.style.width = `${progress}%`;
  }

  open() {
    // Reset progress so it animates from 0 each time the drawer opens
    const progressEl = this.querySelector('.shipping-bar .progress[data-progress]');
    if (progressEl instanceof HTMLElement) {
      progressEl.style.width = '0%';
    }

    this.showDialog();

    requestAnimationFrame(() => {
      const el = this.querySelector('.shipping-bar .progress[data-progress]');
      if (el instanceof HTMLElement) {
        el.getBoundingClientRect(); // force layout flush before transitioning
        this.#animateProgress(el);
      }
    });

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
